// BoothDetailsModal Component (Issue #1159)
// Modal displaying detailed booth information for customers browsing the booth map
// Shows vendor information, booth features, pricing, and navigation options

import React, { useEffect, useRef } from 'react';
import { X, MapPin, Zap, Star, DollarSign, Navigation } from 'lucide-react';
import { BoothMapSlot } from '../types';
import { formatCurrency } from '../utils/currency';

interface BoothDetailsModalProps {
  slot: BoothMapSlot | null;
  isOpen: boolean;
  onClose: () => void;
  fairId?: number;
}

/**
 * BoothDetailsModal - Customer-facing modal for booth information
 *
 * Displays:
 * - Vendor name and business info
 * - Booth number and dimensions
 * - Product types/categories
 * - Vendor rating (if available)
 * - Booth features (power, corner, high traffic, near entrance)
 * - Pricing information (if public)
 * - Distance to nearby amenities (future enhancement)
 * - Links to vendor profile
 *
 * Accessibility:
 * - WCAG 2.1 AA compliant
 * - Keyboard navigation (Escape to close, Tab to navigate)
 * - Focus management (traps focus within modal)
 * - Screen reader announcements
 *
 * Issue #1159: Customer-centric booth map features
 */
const BoothDetailsModal: React.FC<BoothDetailsModalProps> = ({
  slot,
  isOpen,
  onClose,
  fairId,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Focus management - focus close button when modal opens
  useEffect(() => {
    if (isOpen && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [isOpen]);

  // Keyboard navigation - close modal on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Trap focus within modal
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const modal = modalRef.current;
    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0] as HTMLElement;
    const lastFocusable = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey && document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable?.focus();
      } else if (!e.shiftKey && document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable?.focus();
      }
    };

    modal.addEventListener('keydown', handleTabKey as unknown as EventListener);
    return () => modal.removeEventListener('keydown', handleTabKey as unknown as EventListener);
  }, [isOpen]);

  if (!isOpen || !slot) return null;

  const isReserved = !slot.is_available;
  const hasVendorInfo = Boolean(slot.vendor_name || slot.vendor_business_name);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={(e) => {
        // Close modal when clicking backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="booth-details-title"
      data-testid="booth-details-modal"
    >
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2
            id="booth-details-title"
            className="text-2xl font-bold text-gray-900"
            data-testid="booth-details-title"
          >
            Booth {slot.slot_number}
          </h2>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 focus:ring-2 focus:ring-dusty-teal focus:outline-none"
            aria-label="Close booth details"
            data-testid="close-booth-details-button"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-6">
          {/* Availability Status */}
          <div
            className={`px-4 py-3 rounded-lg ${
              isReserved
                ? 'bg-red-50 border border-red-200'
                : 'bg-green-50 border border-green-200'
            }`}
            role="status"
            aria-live="polite"
            data-testid="booth-availability-status"
          >
            <p
              className={`text-sm font-medium ${
                isReserved ? 'text-red-800' : 'text-green-800'
              }`}
            >
              {isReserved ? '🔴 Reserved' : '🟢 Available'}
            </p>
          </div>

          {/* Vendor Information (if reserved) */}
          {isReserved && hasVendorInfo && (
            <div className="space-y-3" data-testid="vendor-information">
              <h3 className="text-lg font-semibold text-gray-900">Vendor Information</h3>
              <div className="space-y-2">
                {slot.vendor_business_name && (
                  <p className="text-gray-900 font-medium">
                    {slot.vendor_business_name}
                  </p>
                )}
                {slot.vendor_name && (
                  <p className="text-gray-600 text-sm">By {slot.vendor_name}</p>
                )}
                {slot.vendor_product_types && slot.vendor_product_types.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {slot.vendor_product_types.map((type, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 text-xs font-medium text-ink bg-moonbone rounded-full"
                        data-testid={`product-type-${type.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {type}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {slot.vendor_id && (
                <a
                  href={`/vendors/${slot.vendor_id}`}
                  className="inline-flex items-center gap-2 text-secondary hover:text-ink font-medium text-sm focus:ring-2 focus:ring-dusty-teal focus:outline-none rounded px-2 py-1"
                  data-testid="view-vendor-profile-link"
                >
                  View Vendor Profile →
                </a>
              )}
            </div>
          )}

          {/* Booth Details */}
          <div className="space-y-3" data-testid="booth-details">
            <h3 className="text-lg font-semibold text-gray-900">Booth Details</h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Dimensions */}
              {slot.dimensions && (
                <div>
                  <dt className="flex items-center gap-2 text-sm font-medium text-gray-500">
                    <MapPin className="w-4 h-4" aria-hidden="true" />
                    Dimensions
                  </dt>
                  <dd className="mt-1 text-base text-gray-900">{slot.dimensions}</dd>
                </div>
              )}

              {/* Slot Type */}
              <div>
                <dt className="text-sm font-medium text-gray-500">Type</dt>
                <dd className="mt-1 text-base text-gray-900 capitalize">
                  {slot.slot_type || 'Standard'}
                </dd>
              </div>

              {/* Base Price */}
              <div>
                <dt className="flex items-center gap-2 text-sm font-medium text-gray-500">
                  <DollarSign className="w-4 h-4" aria-hidden="true" />
                  Base Price
                </dt>
                <dd className="mt-1 text-base text-gray-900 font-semibold">
                  {formatCurrency(slot.base_price)}
                </dd>
              </div>

              {/* Estimated Foot Traffic */}
              {slot.estimated_foot_traffic && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Avg. Daily Visitors</dt>
                  <dd className="mt-1 text-base text-gray-900">
                    ~{slot.estimated_foot_traffic}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Features */}
          <div className="space-y-3" data-testid="booth-features">
            <h3 className="text-lg font-semibold text-gray-900">Features</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {slot.has_power && (
                <div className="flex items-center gap-2 text-gray-700">
                  <Zap className="w-5 h-5 text-yellow-500" aria-hidden="true" />
                  <span>Power Outlet Available</span>
                </div>
              )}
              {slot.corner_booth && (
                <div className="flex items-center gap-2 text-gray-700">
                  <Navigation className="w-5 h-5 text-orange-500" aria-hidden="true" />
                  <span>Corner Booth</span>
                </div>
              )}
              {slot.high_traffic_area && (
                <div className="flex items-center gap-2 text-gray-700">
                  <Star className="w-5 h-5 text-purple-500" aria-hidden="true" />
                  <span>High Traffic Area</span>
                </div>
              )}
              {slot.near_entrance && (
                <div className="flex items-center gap-2 text-gray-700">
                  <MapPin className="w-5 h-5 text-cyan-500" aria-hidden="true" />
                  <span>Near Entrance</span>
                </div>
              )}
            </div>
            {!slot.has_power && !slot.corner_booth && !slot.high_traffic_area && !slot.near_entrance && (
              <p className="text-sm text-gray-500 italic">Standard booth with no special features</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            {!isReserved && (
              <a
                href={`/fairs/${fairId}/slots`}
                className="flex-1 px-6 py-3 bg-primary text-white text-center font-medium rounded-lg hover:bg-primary-dark focus:ring-2 focus:ring-dusty-teal focus:outline-none"
                data-testid="reserve-booth-button"
              >
                Reserve This Booth
              </a>
            )}
            <button
              onClick={onClose}
              className={`px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:outline-none ${
                !isReserved ? '' : 'flex-1'
              }`}
              data-testid="close-modal-button"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoothDetailsModal;
